import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryObjectUploadContext } from '../../context';
import {
  type OnChangeMatchingRuleHandler,
  MatchingRuleSettings,
} from './MatchingRuleSettings';
import {
  StyledMatchingRuleList,
  StyledAddRuleButton,
  StyledLastExecutableRuleWarning,
  StyledWarningWrapper,
} from './styles';
import { StyledLoader } from 'pages/SmartConnectors/SmartConnector/SmartConnectorWizard/steps/styles';
import { canFieldBeMatchedToArray } from '../../helpers';
import {
  KIZEN_ID_OPTION,
  SMART_FIELD_TYPES,
} from 'pages/SmartConnectors/constants';
import type {
  CSVFlowLoad,
  GenericOption,
  SmartConnectorFlowLoad,
} from 'pages/SmartConnectors/types';
import { useLastExecutableRuleWarning } from './useLastExecutableRuleWarning';
import { isCSVLoadStep } from 'pages/SmartConnectors/SmartConnector/helpers';

const isNewOrCurrentLoad = (
  load: SmartConnectorFlowLoad,
  id: string
): load is CSVFlowLoad => {
  return (id === 'new' && !load.id) || load.id === id;
};

export const MatchingRulesList = () => {
  const { t } = useTranslation();
  const {
    smartConnector,
    customObject,
    loadId,
    loadIndex,
    stepData,
    setStepData,
    metaData,
    loading,
    setErrors,
    allVariables,
  } = useContext(PrimaryObjectUploadContext);

  useEffect(() => {
    const hasMatchingRules =
      loadId !== 'new' &&
      Boolean(
        smartConnector.flow.loads?.find(
          (load): load is CSVFlowLoad => load.id === loadId
        )?.matching_rules?.length
      );

    if (!hasMatchingRules) {
      const uniqueIdentifierField = customObject?.fields?.find(
        ({ name }) => name === 'name' || name === 'email'
      )?.id;

      const matchingRules = metaData.matching_rules;

      setStepData((prev) => ({
        ...prev,
        loads: (prev.loads || []).map((load) =>
          (loadId === 'new' && !load.id) || loadId === load?.id
            ? {
                ...load,
                matching_rules: [
                  {
                    id: `new_${Date.now()}`,
                    order: 0,
                    field: uniqueIdentifierField,
                    is_match_by_kizen_id: false,
                    variable: null,
                    no_match_action: matchingRules.no_match_actions[0]?.value,
                    single_match_action:
                      matchingRules.single_match_actions[0]?.value,
                    multiple_match_action:
                      matchingRules.multiple_match_actions[0]?.value,
                    match_archive_action:
                      matchingRules.match_archive_actions[0]?.value,
                  },
                ],
              }
            : load
        ),
      }));
    }
  }, [setStepData, customObject, loadId, metaData, smartConnector.flow.loads]);

  const onChangeMatchingRuleSettings: OnChangeMatchingRuleHandler = ({
    id,
    prop,
    value,
  }) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (isNewOrCurrentLoad(load, loadId)) {
          const patch = { [prop]: value };

          if (prop === 'field') {
            patch.variable = null;
            patch.is_match_by_kizen_id = !value;
          }

          return {
            ...load,
            matching_rules: (load.matching_rules || []).map((rule) =>
              rule.id === id ? { ...rule, ...patch } : rule
            ),
          };
        }
        return load;
      }),
    }));
  };

  const onDeleteMatchingRule = (id: string, index: number) => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load, i) => {
        if (isCSVLoadStep(load) && i === loadIndex) {
          return {
            ...load,
            matching_rules: (load.matching_rules || []).filter(
              (rule) => rule.id !== id
            ),
          };
        }
        return load;
      }),
    }));
    setErrors((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load, i) => {
        if (i === loadIndex) {
          return {
            ...load,
            matching_rules: (load.matching_rules || []).filter(
              (_, i) => index !== i
            ),
          };
        }
        return load;
      }),
    }));
  };

  const [load, rules] = useMemo(() => {
    const load = stepData.loads?.find((load): load is CSVFlowLoad =>
      isNewOrCurrentLoad(load, loadId)
    );
    const rules = load?.matching_rules || [];
    return [load, rules];
  }, [stepData.loads, loadId]);

  const onReorderMatchingRule = (index: number, offset: 1 | -1) => {
    const nextIndex = index + offset;
    if (!load || nextIndex < 0 || nextIndex >= load.matching_rules.length) {
      return;
    }

    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load, i) => {
        if (isNewOrCurrentLoad(load, loadId)) {
          const matchingRules = [...load.matching_rules];
          const [currentRule] = matchingRules.splice(index, 1);
          matchingRules.splice(nextIndex, 0, currentRule);
          return {
            ...load,
            matching_rules: matchingRules,
          };
        }
        return load;
      }),
    }));

    setErrors((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load, i) => {
        if (i === loadIndex) {
          const matchingRuleErrors = [...(load?.matching_rules || [])];
          const [currentError] = matchingRuleErrors.splice(index, 1);
          matchingRuleErrors.splice(nextIndex, 0, currentError);
          return {
            ...load,
            matching_rules: matchingRuleErrors,
          };
        }
        return load;
      }),
    }));
  };

  const onAddMatchingRule = () => {
    setStepData((prev) => ({
      ...prev,
      loads: (prev.loads || []).map((load) => {
        if (isNewOrCurrentLoad(load, loadId)) {
          return {
            ...load,
            matching_rules: [
              ...(load.matching_rules || []),
              {
                id: `new_${Date.now()}`,
                order: 0,
                field: null,
                is_match_by_kizen_id: false,
                variable: null,
                no_match_action:
                  metaData.matching_rules.no_match_actions[0]?.value,
                single_match_action:
                  metaData.matching_rules.single_match_actions[0]?.value,
                multiple_match_action:
                  metaData.matching_rules.multiple_match_actions[0]?.value,
                match_archive_action:
                  metaData.matching_rules.match_archive_actions[0]?.value,
              },
            ],
          };
        }
        return load;
      }),
    }));
  };

  const fieldOptions = useMemo<
    { label: string; options: GenericOption[] }[]
  >(() => {
    const sortedCategories = [...(customObject?.fieldCategories || [])].sort(
      ({ order: a }, { order: b }) => a - b
    );
    const sortedFields = (customObject?.fields || [])
      .filter(
        ({ fieldType }) =>
          fieldType !== SMART_FIELD_TYPES.longtext &&
          fieldType !== SMART_FIELD_TYPES.kizen_id &&
          SMART_FIELD_TYPES[fieldType]
      )
      .sort(({ order: a = 0 }, { order: b = 0 }) => a - b);
    return sortedCategories.map((category, i) => {
      const fieldOptions: GenericOption[] = i === 0 ? [KIZEN_ID_OPTION] : [];

      return {
        label: category.name,
        options: sortedFields.reduce((acc, field) => {
          if (field.category === category.id) {
            acc.push({
              value: field.id,
              label: field.displayName,
            });
          }
          return acc;
        }, fieldOptions),
      };
    });
  }, [customObject?.fields, customObject?.fieldCategories]);

  const singleVariables = useMemo(
    () => allVariables.filter((variable) => !variable.is_array),
    [allVariables]
  );

  const fieldsByIsArray = useMemo(() => {
    return (customObject?.fields || [])?.reduce(
      (acc, field) => {
        if (canFieldBeMatchedToArray(field)) {
          acc[field.id] = true;
        }
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [customObject?.fields]);

  const { lastExecutableRuleIndex, listRef, warningPosition } =
    useLastExecutableRuleWarning(rules);

  return (
    <StyledLoader loading={loading}>
      <StyledMatchingRuleList ref={listRef}>
        {rules.map((rule, i) => (
          <MatchingRuleSettings
            key={rule.id}
            index={i}
            isLastIndex={i === rules.length - 1}
            rule={rule}
            onChange={onChangeMatchingRuleSettings}
            onDelete={onDeleteMatchingRule}
            onReorder={onReorderMatchingRule}
            fieldOptions={fieldOptions}
            variables={
              rule.field && fieldsByIsArray[rule.field]
                ? allVariables
                : singleVariables
            }
            isLastExecutableRule={i === lastExecutableRuleIndex}
          />
        ))}
        {lastExecutableRuleIndex !== null && warningPosition ? (
          <StyledWarningWrapper topOffset={warningPosition}>
            <StyledLastExecutableRuleWarning>
              {t(
                'Subsequent rules are not reachable since none of the Upload Functions of the previous step can proceed to the next step.'
              )}
            </StyledLastExecutableRuleWarning>
          </StyledWarningWrapper>
        ) : null}
      </StyledMatchingRuleList>
      <StyledAddRuleButton variant="text" onClick={onAddMatchingRule}>
        {t('+ Add Field Match Rule')}
      </StyledAddRuleButton>
    </StyledLoader>
  );
};
